/**
 * Persists an in-progress "post a link" draft across the sign-up round trip.
 *
 * A guest fills the form, signs up (a full page navigation through Clerk), and
 * returns — React state is long gone by then, so the draft lives in
 * `sessionStorage` (per-tab, cleared when the tab closes; never `localStorage`,
 * since the draft holds a referral URL).
 *
 * A guest draft is claimable by whoever signs in next in that tab — that is the
 * flow being fixed. A draft written while signed in is tied to that user id and
 * is discarded for anyone else.
 */

import type { ListingType } from '@/lib/types/listing'

const KEY = 'dizcant:drop-draft'
const VERSION = 1
const TTL_MS = 24 * 60 * 60 * 1000

export interface DropDraft {
  type: ListingType
  destinationUrl: string
  brand: string
  category: string
  categoryOther: string
  country: string
  benefitClaim: string
  expiresAt: string
}

interface Envelope {
  version: number
  savedAt: number
  ownerKey: string
  draft: DropDraft
}

function ownerKeyFor(userId: string | null | undefined): string {
  return userId ?? 'guest'
}

function isEmpty(draft: DropDraft): boolean {
  return (
    !draft.destinationUrl.trim() &&
    !draft.brand.trim() &&
    !draft.category &&
    !draft.categoryOther.trim() &&
    !draft.country &&
    !draft.benefitClaim.trim()
  )
}

export function saveDropDraft(
  draft: DropDraft,
  userId: string | null | undefined,
): void {
  try {
    if (isEmpty(draft)) {
      sessionStorage.removeItem(KEY)
      return
    }
    const envelope: Envelope = {
      version: VERSION,
      savedAt: Date.now(),
      ownerKey: ownerKeyFor(userId),
      draft,
    }
    sessionStorage.setItem(KEY, JSON.stringify(envelope))
  } catch {
    // Storage unavailable — the draft simply won't persist.
  }
}

/**
 * Read a persisted draft for the current user.
 *
 * `authSettled` distinguishes "this draft belongs to someone else" (purge it)
 * from "we don't know who's signed in yet" (leave it, ask again once Clerk
 * loads). Guest drafts are always returned regardless.
 */
export function loadDropDraft(
  userId: string | null | undefined,
  { authSettled = true }: { authSettled?: boolean } = {},
): DropDraft | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) {
      return null
    }
    const envelope = JSON.parse(raw) as Partial<Envelope>
    const draft = envelope.draft
    const stale =
      envelope.version !== VERSION ||
      typeof envelope.savedAt !== 'number' ||
      Date.now() - envelope.savedAt > TTL_MS ||
      !draft
    if (stale) {
      sessionStorage.removeItem(KEY)
      return null
    }

    const wrongOwner =
      envelope.ownerKey !== 'guest' && envelope.ownerKey !== ownerKeyFor(userId)
    if (wrongOwner) {
      if (authSettled) {
        sessionStorage.removeItem(KEY)
      }
      return null
    }
    return draft as DropDraft
  } catch {
    return null
  }
}

export function clearDropDraft(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}
